import { X, Printer, Check, Zap, Award, Star } from 'lucide-react';
import { MembershipPlan } from '../pages/PlansPreviewPage';

interface PlanShowcaseModalProps {
    isOpen: boolean;
    onClose: () => void;
    plans: MembershipPlan[];
    gymName: string;
}

export default function PlanShowcaseModal({ isOpen, onClose, plans, gymName }: PlanShowcaseModalProps) {
    if (!isOpen) return null;

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="fixed inset-0 z-[2000] bg-zinc-950 flex flex-col overflow-hidden animate-in fade-in duration-300">
            {/* Header - Hidden during print */}
            <div className="flex items-center justify-between p-6 border-b border-white/10 bg-zinc-950 print:hidden shrink-0">
                <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-900/20">
                        <Zap className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h2 className="text-xl font-black text-white uppercase tracking-tight leading-none">{gymName || 'Gym'}</h2>
                        <p className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] mt-1.5">Official Membership Showcase</p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={handlePrint}
                        className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-white/10 transition-all active:scale-95 group"
                        title="Print Catalog"
                    >
                        <Printer className="w-5 h-5 group-hover:scale-110 transition-transform" />
                    </button>
                    <button
                        onClick={onClose}
                        className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-red-500/10 hover:border-red-500/20 hover:text-red-500 transition-all active:scale-95 group"
                        title="Close Showcase"
                    >
                        <X className="w-6 h-6 group-hover:rotate-90 transition-transform" />
                    </button>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto bg-zinc-950 p-6 sm:p-12 print:p-0 print:overflow-visible no-scrollbar">
                <div className="max-w-7xl mx-auto space-y-12 print:space-y-8">
                    {/* Hero Section */}
                    <div className="text-center space-y-4 print:space-y-2">
                        <h1 className="text-5xl sm:text-7xl font-black text-white tracking-tighter print:text-zinc-950 print:text-4xl">
                            CHOOSE YOUR <span className="text-blue-500 print:text-blue-700 font-outline-2">LEVEL</span>
                        </h1>
                        <p className="text-sm font-black text-white/30 uppercase tracking-[0.4em] print:text-zinc-500 print:text-xs">
                            Tailored fitness solutions for your goals
                        </p>
                    </div>

                    {/* Plans Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 print:grid-cols-2 print:gap-4 print:text-zinc-950">
                        {plans.map((plan, idx) => (
                            <div
                                key={plan.id}
                                className={`
                  relative group flex flex-col bg-zinc-900 border border-white/5 rounded-[2.5rem] p-8 transition-all hover:border-blue-500/50 hover:bg-zinc-900/50 hover:-translate-y-2
                  print:bg-white print:border-zinc-200 print:rounded-3xl print:shadow-none print:hover:translate-y-0 print:p-6
                  ${idx % 3 === 1 ? 'border-blue-500/20' : ''}
                `}
                            >
                                {/* Decorative background element */}
                                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full blur-[60px] -mr-16 -mt-16 group-hover:bg-blue-500/10 transition-all opacity-50 print:hidden" />

                                <div className="relative space-y-6">
                                    {/* Icon & Name */}
                                    <div className="flex items-start justify-between">
                                        <div className="space-y-1">
                                            <h3 className="text-2xl font-black text-white uppercase tracking-tight group-hover:text-blue-500 transition-colors print:text-zinc-950 print:text-xl">
                                                {plan.name}
                                            </h3>
                                            <div className="h-1 w-12 bg-blue-500 rounded-full print:bg-zinc-300" />
                                        </div>
                                        <div className={`p-3 rounded-2xl bg-white/5 text-blue-500 print:bg-zinc-50 print:text-zinc-400`}>
                                            {idx % 3 === 0 ? <Zap className="w-6 h-6" /> : idx % 3 === 1 ? <Award className="w-6 h-6" /> : <Star className="w-6 h-6" />}
                                        </div>
                                    </div>

                                    {/* Pricing */}
                                    <div className="space-y-1">
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-4xl font-black text-white tracking-tight print:text-zinc-950 print:text-3xl">₹{plan.price}</span>
                                            {plan.durationMonths && (
                                                <span className="text-sm font-black text-white/20 uppercase tracking-widest print:text-zinc-500">/ {plan.durationMonths} MO</span>
                                            )}
                                        </div>
                                        {plan.durationMonths && plan.price && (
                                            <p className="text-[10px] font-bold text-white/10 uppercase tracking-[0.2em] print:text-zinc-400">
                                                ₹{Math.round(plan.price / plan.durationMonths)} effective per month
                                            </p>
                                        )}
                                    </div>

                                    {/* Features */}
                                    <div className="space-y-4 py-6 border-y border-white/5 print:border-zinc-100 print:py-4">
                                        {plan.description && (
                                            <p className="text-xs font-bold text-white/40 leading-relaxed print:text-zinc-600 italic">
                                                "{plan.description}"
                                            </p>
                                        )}

                                        <div className="space-y-3">
                                            <div className="flex items-center gap-3 text-white/60 print:text-zinc-700">
                                                <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                                <span className="text-xs font-black uppercase tracking-wider">{plan.durationMonths} Months Access</span>
                                            </div>

                                            {plan.features?.pauseEnabled && (
                                                <div className="flex items-center gap-3 text-white/60 print:text-zinc-700">
                                                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                                    <span className="text-xs font-black uppercase tracking-wider">
                                                        {plan.features.pauseRules?.maxPauses || 'Unlimited'} Membership Pauses
                                                    </span>
                                                </div>
                                            )}

                                            {plan.features?.externalGymAccess?.enabled && (
                                                <div className="flex items-center gap-3 text-white/60 print:text-zinc-700">
                                                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                                    <span className="text-xs font-black uppercase tracking-wider">
                                                        Multi-gym access ({plan.features.externalGymAccess.days} days)
                                                    </span>
                                                </div>
                                            )}

                                            {plan.features?.loyaltyPoints?.enabled && (
                                                <div className="flex items-center gap-3 text-white/60 print:text-zinc-700">
                                                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                                    <span className="text-xs font-black uppercase tracking-wider">
                                                        Loyalty Rewards Program
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>


                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Quote Section */}
                    <div className="py-20 text-center print:py-10">
                        <h3 className="text-2xl font-black text-white/20 uppercase tracking-[0.5em] print:text-zinc-300 print:text-sm">Powering your fitness journey</h3>
                        <div className="mt-4 flex flex-col items-center gap-2">
                            <div className="flex items-center justify-center gap-3 text-[10px] font-black text-blue-500/40 uppercase tracking-[0.3em] print:text-zinc-400">
                                <span className="w-12 h-px bg-blue-500/20 print:bg-zinc-200"></span>
                                {gymName || 'GYMPE'}
                                <span className="w-12 h-px bg-blue-500/20 print:bg-zinc-200"></span>
                            </div>
                            <p className="text-[7px] font-black text-white/10 uppercase tracking-[0.5em] print:text-zinc-300">powered by GYMPE</p>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
        @media print {
          @page {
            size: A4;
            margin: 15mm;
          }
          body {
            background: white !important;
            color: black !important;
          }
          .no-scrollbar::-webkit-scrollbar {
            display: none;
          }
        }
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .font-outline-2 {
          -webkit-text-stroke: 1px rgba(255,255,255,0.3);
          color: transparent;
        }
        @media print {
          .font-outline-2 {
            -webkit-text-stroke: 1px rgba(0,0,0,0.5);
            color: black;
          }
        }
      `}</style>
        </div>
    );
}
